import React, { useEffect, useRef } from 'react';

const Hero = () => {
    const typewriterRef = useRef(null);
    const businessTypes = ["clínicas", "barberías", "spas", "peluquerías", "estéticas"];

    useEffect(() => {
        let currentIndex = 0;
        let isDeleting = false;
        let currentText = '';
        let typingSpeed = 100;

        const type = () => {
            const currentBusinessType = businessTypes[currentIndex];

            if (isDeleting) {
                currentText = currentBusinessType.substring(0, currentText.length - 1);
                typingSpeed = 50;
            } else {
                currentText = currentBusinessType.substring(0, currentText.length + 1);
                typingSpeed = 100;
            }

            if (typewriterRef.current) {
                typewriterRef.current.textContent = currentText;
            }

            if (!isDeleting && currentText === currentBusinessType) {
                typingSpeed = 1500; // Pause at the end of typing
                isDeleting = true;
            } else if (isDeleting && currentText === '') {
                isDeleting = false;
                currentIndex = (currentIndex + 1) % businessTypes.length;
                typingSpeed = 500; // Pause before typing new word
            }

            setTimeout(type, typingSpeed);
        };

        setTimeout(type, 1000);

    }, []);

    return (
        <section className="relative pt-32 md:pt-40 pb-24 overflow-hidden bg-white" id="inicio">
            {/* Background elements for desktop only */}
            <div className="absolute top-0 right-0 w-[40%] h-full bg-primary clip-hero-shape hidden md:block"></div>
            <div className="absolute bottom-0 left-0 h-64 w-64 bg-primary rounded-tl-[100px] transform translate-x-[-50%] hidden md:block"></div>
            <div className="absolute bottom-28 left-28 w-40 h-40 bg-secondary rounded-full opacity-90 transform -translate-x-1/2 -translate-y-1/2 hidden md:block"></div>

            {/* Mobile background - crosses behind the calendar */}
            <div className="absolute top-[40%] right-0 w-2/3 h-60 bg-primary md:hidden"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                    {/* Left column (text) */}
                    <div className="text-left max-w-lg">
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-secondary animate-fadeIn">
                            <span className="block mb-2">Solución Digital</span>
                            <div className="text-primary relative inline-block">
                                <div className="flex flex-wrap items-baseline">
                                    <span className="mr-2">para</span>
                                    <div className="relative inline-block typewriter-container">
                                        <span ref={typewriterRef} className="typewriter"></span>
                                        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></span>
                                    </div>
                                </div>
                            </div>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-700 mb-10 animate-fadeIn animation-delay-200 max-w-full">
                            La plataforma completa para negocios de servicios, citas y reservas
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn animation-delay-400">
                            <a href="#demo" className="px-8 py-4 rounded-lg bg-primary text-white font-semibold text-lg shadow-lg transition-transform duration-300 hover:scale-105 text-center">
                                Probar Demo en Vivo
                            </a>
                            <a href="#planes" className="px-8 py-4 rounded-lg bg-secondary text-white font-semibold text-lg transition-transform duration-300 hover:scale-105 text-center">
                                Ver Planes
                            </a>
                        </div>
                    </div>

                    {/* Right column (image) */}
                    <div className="relative animate-fadeIn animation-delay-600 mt-8 md:mt-0">
                        <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-secondary opacity-20 rounded-tl-[40px] hidden md:block"></div>

                        {/* Device mockup - adjusted angle for more professional look */}
                        <div className="relative z-10 transform sm:rotate-[3deg] sm:hover:rotate-[1deg] transition-all duration-500">
                            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mx-auto max-w-md border-8 border-gray-100 transform hover:scale-105 transition-all duration-500">
                                {/* App mockup image */}
                                <div className="aspect-[9/16] bg-gray-50 relative overflow-hidden">
                                    <div className="absolute inset-0 flex flex-col">
                                        {/* App header */}
                                        <div className="bg-secondary p-4 flex items-center">
                                            <div className="w-8 h-8 rounded bg-primary flex items-center justify-center text-white font-bold text-sm">G</div>
                                            <div className="ml-3 text-white font-semibold">Panel de Reservas</div>
                                        </div>

                                        {/* App content */}
                                        <div className="flex-1 p-4">
                                            {/* Calendar header */}
                                            <div className="mb-4">
                                                <div className="text-secondary font-bold">Enero {new Date().getFullYear()}</div>
                                                <div className="flex justify-between text-xs text-gray-400 mt-2">
                                                    <span>L</span>
                                                    <span>M</span>
                                                    <span>X</span>
                                                    <span>J</span>
                                                    <span>V</span>
                                                    <span>S</span>
                                                    <span>D</span>
                                                </div>
                                            </div>

                                            {/* Calendar days */}
                                            <div className="grid grid-cols-7 gap-1 mb-6">
                                                {Array.from({ length: 31 }).map((_, i) => (
                                                    <div
                                                        key={i}
                                                        className={`aspect-square flex items-center justify-center text-xs rounded-full ${i === 14 ? 'bg-primary text-white' :
                                                            i === 22 ? 'bg-secondary text-white' : 'hover:bg-gray-100'
                                                            }`}
                                                    >
                                                        {i + 1}
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Appointments */}
                                            <div className="space-y-3">
                                                <div className="bg-primary/10 p-3 rounded-lg border-l-4 border-primary">
                                                    <div className="text-xs text-gray-500">10:00 - 11:00</div>
                                                    <div className="font-semibold text-gray-700">Servicio Premium</div>
                                                </div>
                                                <div className="bg-secondary/10 p-3 rounded-lg border-l-4 border-secondary">
                                                    <div className="text-xs text-gray-500">12:30 - 13:30</div>
                                                    <div className="font-semibold text-gray-700">Consulta Regular</div>
                                                </div>
                                                <div className="bg-gray-100 p-3 rounded-lg">
                                                    <div className="text-xs text-gray-500">15:00 - 16:00</div>
                                                    <div className="font-semibold text-gray-700">Servicio Express</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom clip path for hero shape */}
            <style jsx="true">{`
                .clip-hero-shape {
                    clip-path: polygon(100% 0, 100% 100%, 0 100%, 30% 0);
                }
                
                .typewriter::after {
                    content: '|';
                    animation: blink 1s step-end infinite;
                }
                
                @keyframes blink {
                    from, to { opacity: 1; }
                    50% { opacity: 0; }
                }
                
                .animation-delay-200 {
                    animation-delay: 0.2s;
                }
                
                .animation-delay-400 {
                    animation-delay: 0.4s;
                }
                
                .animation-delay-600 {
                    animation-delay: 0.6s;
                }

                /* Typewriter styles with fixed height to prevent layout shifts */
                .typewriter-container {
                    display: inline-flex;
                    align-items: flex-start;
                }
                
                .typewriter {
                    display: inline-block;
                    max-width: 100%;
                    overflow-wrap: anywhere;
                    word-break: break-word;
                    hyphens: auto;
                }
            `}</style>
        </section>
    );
};

export default Hero;